import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Building2, Plus, Edit2, X, Loader2, CheckCircle2, Power,
  Trash2, ShieldCheck, Palette, LayoutGrid
} from "lucide-react";
import { invalidarUnidadesCache } from "../../hooks/useBusinessUnits";

// Debe coincidir con SECCIONES_CANONICAS del backend
const SECCIONES = [
  { key: "resumen", label: "Resumen" },
  { key: "flujo_operativo", label: "Operación" },
  { key: "finanzas", label: "Finanzas" },
  { key: "menu", label: "Menú" },
  { key: "personal", label: "Personal" },
  { key: "servicios", label: "Servicios" },
  { key: "montaje", label: "Montaje" },
  { key: "itinerario", label: "Itinerario" },
  { key: "orden_dia", label: "Orden del día" },
  { key: "documentos", label: "Documentos" },
  { key: "proveedores", label: "Proveedores" },
  { key: "datos", label: "Datos" },
  { key: "notas", label: "Notas" },
];

const TIPOS = [
  { key: "instalaciones", label: "Renta de instalaciones" },
  { key: "planeacion", label: "Planeación / decoración" },
  { key: "catering", label: "Catering / alimentos" },
  { key: "servicios", label: "Servicios especializados" },
  { key: "personalizada", label: "Otra / personalizada" },
];

const initialForm = {
  slug: "",
  nombre: "",
  descripcion_corta: "",
  descripcion_larga: "",
  tipo: "personalizada",
  color: "#6b7280",
  color_soft: "#f3f4f6",
  color_dark: "#374151",
  icon: "Building2",
  logo_url: "",
  cta: "Ver más",
  activa: true,
  orden: 100,
  secciones_permitidas: ["resumen", "datos", "notas"],
  bullets: [],
};

const AdminUnidadesConfig = () => {
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [guardando, setGuardando] = useState(false);

  const [mostrarTest, setMostrarTest] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business-units?incluir_inactivas=true&mostrar_test=${mostrarTest}`);
      setUnidades(data.unidades || []);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [mostrarTest]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNueva = () => {
    setEditando(null);
    setForm(initialForm);
    setShowModal(true);
  };

  const openEditar = (u) => {
    setEditando(u.slug);
    setForm({
      slug: u.slug,
      nombre: u.nombre || "",
      descripcion_corta: u.descripcion_corta || "",
      descripcion_larga: u.descripcion_larga || "",
      tipo: u.tipo || "personalizada",
      color: u.color || "#6b7280",
      color_soft: u.color_soft || "#f3f4f6",
      color_dark: u.color_dark || "#374151",
      icon: u.icon || "Building2",
      logo_url: u.logo_url || "",
      cta: u.cta || "Ver más",
      activa: u.activa !== false,
      orden: u.orden ?? 100,
      secciones_permitidas: u.secciones_permitidas || ["resumen"],
      bullets: u.bullets || [],
    });
    setShowModal(true);
  };

  const toggleSeccion = (key) => {
    setForm(f => ({
      ...f,
      secciones_permitidas: f.secciones_permitidas.includes(key)
        ? f.secciones_permitidas.filter(x => x !== key)
        : [...f.secciones_permitidas, key],
    }));
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es requerido"); return; }
    if (!editando && !form.slug.trim()) { toast.error("El slug es requerido para crear"); return; }
    if (form.secciones_permitidas.length === 0) { toast.error("Selecciona al menos una sección"); return; }
    setGuardando(true);
    try {
      if (editando) {
        await axios.put(`${API}/business-units/${editando}`, form);
        toast.success("Unidad actualizada");
      } else {
        await axios.post(`${API}/business-units`, form);
        toast.success("Unidad creada");
      }
      invalidarUnidadesCache();
      setShowModal(false);
      fetchAll();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGuardando(false);
    }
  };

  const toggleActiva = async (u) => {
    try {
      await axios.patch(`${API}/business-units/${u.slug}/toggle`);
      toast.success(`Unidad ${u.activa ? "desactivada" : "activada"}`);
      invalidarUnidadesCache();
      fetchAll();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar la unidad "${u.nombre}"?\n\nSolo se pueden eliminar unidades personalizadas.`)) return;
    try {
      await axios.delete(`${API}/business-units/${u.slug}`);
      toast.success("Unidad eliminada");
      invalidarUnidadesCache();
      fetchAll();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto pb-12" data-testid="admin-unidades-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="uppercase tracking-[0.18em] text-xs" style={{ color: "var(--brand-primary)" }}>Administración</p>
          <h1 className="font-display text-3xl md:text-4xl" style={{ color: "var(--brand-text)" }}>
            Unidades de Negocio
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--brand-text-muted)" }}>
            Configura las empresas que operan dentro de tu plataforma. Cada unidad ve solo las secciones que le corresponden dentro del expediente del evento.
          </p>
        </div>
        <button
          onClick={openNueva}
          className="eo-btn eo-btn-primary"
          data-testid="btn-nueva-unidad"
        >
          <Plus className="w-4 h-4" /> Nueva unidad
        </button>
      </div>

      {/* Toggle mostrar test units (default oculto) */}
      <div className="flex items-center gap-2 text-xs" style={{ color: "var(--brand-text-muted)" }}>
        <input
          type="checkbox"
          checked={mostrarTest}
          onChange={(e) => setMostrarTest(e.target.checked)}
          id="chk-mostrar-test"
          data-testid="chk-mostrar-test-units"
        />
        <label htmlFor="chk-mostrar-test">Mostrar unidades de prueba (Test Unidad E2E)</label>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center" style={{ color: "var(--brand-text-muted)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lista-unidades">
          {unidades.map(u => (
            <div
              key={u.slug}
              className="eo-card p-5"
              data-testid={`unidad-card-${u.slug}`}
              style={{ borderTop: `4px solid ${u.color || "var(--brand-primary)"}` }}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: u.color_soft || "var(--brand-surface-muted)" }}
                >
                  <Building2 className="w-6 h-6" style={{ color: u.color_dark || "var(--brand-text)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl truncate" style={{ color: "var(--brand-text)" }}>{u.nombre}</h3>
                    {u.es_celebra && (
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" }}>
                        Núcleo CELEBRA
                      </span>
                    )}
                    {!u.activa && (
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">
                        Inactiva
                      </span>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>
                    <code>/{u.slug}</code> · {u.tipo || "personalizada"}
                  </p>
                  {u.descripcion_corta && (
                    <p className="text-sm mt-1" style={{ color: "var(--brand-text-muted)" }}>
                      {u.descripcion_corta}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2 flex-wrap mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>
                  Secciones visibles ({(u.secciones_permitidas || []).length}):
                </span>
                {(u.secciones_permitidas || []).map(s => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" }}>
                    {SECCIONES.find(x => x.key === s)?.label || s}
                  </span>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => openEditar(u)}
                  className="eo-btn eo-btn-outline text-xs"
                  data-testid={`btn-editar-${u.slug}`}
                >
                  <Edit2 className="w-3.5 h-3.5" /> Editar
                </button>
                <button
                  onClick={() => toggleActiva(u)}
                  className="eo-btn eo-btn-outline text-xs"
                  data-testid={`btn-toggle-${u.slug}`}
                >
                  <Power className="w-3.5 h-3.5" /> {u.activa ? "Desactivar" : "Activar"}
                </button>
                {!u.es_celebra && (
                  <button
                    onClick={() => eliminar(u)}
                    className="eo-btn eo-btn-outline text-xs text-red-600"
                    data-testid={`btn-eliminar-${u.slug}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setShowModal(false)}>
          <div
            className="eo-card w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="modal-unidad"
          >
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--brand-border)" }}>
              <div>
                <p className="uppercase tracking-[0.18em] text-[10px]" style={{ color: "var(--brand-primary)" }}>
                  {editando ? "Editar" : "Nueva"} unidad de negocio
                </p>
                <h3 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>
                  {form.nombre || "Sin nombre"}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-stone-100" data-testid="btn-cerrar-modal">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Slug */}
              <div>
                <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>
                  Slug (identificador único, sin espacios)
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                  disabled={!!editando}
                  placeholder="ej. fotografia, dj-eventos, floreria"
                  className="eo-input w-full"
                  data-testid="input-slug"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    className="eo-input w-full"
                    placeholder="ej. Fotografía Aurora"
                    data-testid="input-nombre"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Tipo</label>
                  <select
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    className="eo-input w-full"
                    data-testid="input-tipo"
                  >
                    {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Descripción corta</label>
                <input
                  type="text"
                  value={form.descripcion_corta}
                  onChange={(e) => setForm({ ...form, descripcion_corta: e.target.value })}
                  className="eo-input w-full"
                  placeholder="Ej. Fotografía profesional para eventos"
                />
              </div>

              {/* Colores */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Color principal</label>
                  <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-full h-10 rounded" data-testid="input-color" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Color suave</label>
                  <input type="color" value={form.color_soft} onChange={(e) => setForm({ ...form, color_soft: e.target.value })} className="w-full h-10 rounded" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Color oscuro</label>
                  <input type="color" value={form.color_dark} onChange={(e) => setForm({ ...form, color_dark: e.target.value })} className="w-full h-10 rounded" />
                </div>
              </div>

              {/* Logo url */}
              <div>
                <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Logo (URL opcional)</label>
                <input
                  type="text"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  className="eo-input w-full"
                  placeholder="https://…/logo.png"
                />
              </div>

              {/* Secciones */}
              <div>
                <label className="text-[10px] uppercase tracking-wider block mb-2" style={{ color: "var(--brand-text-muted)" }}>
                  <LayoutGrid className="w-3 h-3 inline mr-1" />
                  Secciones del expediente visibles para esta unidad
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {SECCIONES.map(s => (
                    <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-stone-50">
                      <input
                        type="checkbox"
                        checked={form.secciones_permitidas.includes(s.key)}
                        onChange={() => toggleSeccion(s.key)}
                        data-testid={`checkbox-seccion-${s.key}`}
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Activa */}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.activa}
                  onChange={(e) => setForm({ ...form, activa: e.target.checked })}
                  data-testid="input-activa"
                />
                Activa (visible en el expediente)
              </label>
            </div>

            <div className="flex justify-end gap-2 p-5 border-t" style={{ borderColor: "var(--brand-border)" }}>
              <button onClick={() => setShowModal(false)} className="eo-btn eo-btn-outline">Cancelar</button>
              <button
                onClick={guardar}
                disabled={guardando}
                className="eo-btn eo-btn-primary"
                data-testid="btn-guardar-unidad"
              >
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {editando ? "Actualizar" : "Crear"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUnidadesConfig;
